import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/common/StatusBadge';
import { platformService } from '@/services/platform';
import { formatDate } from '@/lib/utils';
import type { ConnectionStatus } from '@/types/trading';

export function PlatformStatus() {
  const queryClient = useQueryClient();

  const { data: status, isLoading } = useQuery({
    queryKey: ['platform', 'status'],
    queryFn: () => platformService.getStatus(),
    refetchInterval: 60000, // Check every minute
  });

  const syncMutation = useMutation({
    mutationFn: () => platformService.syncPositions(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform'] });
      void queryClient.invalidateQueries({ queryKey: ['portfolio'] });
    },
  });

  const statusConfig: Record<
    ConnectionStatus,
    { icon: typeof CheckCircle; color: 'success' | 'neutral' | 'info' | 'error'; label: string }
  > = {
    connected: { icon: CheckCircle, color: 'success', label: 'Connected' },
    disconnected: { icon: XCircle, color: 'neutral', label: 'Disconnected' },
    connecting: { icon: Loader2, color: 'info', label: 'Connecting...' },
    error: { icon: AlertCircle, color: 'error', label: 'Error' },
  };

  const config = statusConfig[status?.status || 'disconnected'];
  const Icon = config.icon;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse h-20 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">Platform Connection</CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending || status?.status !== 'connected'}
        >
          <RefreshCw className={`h-4 w-4 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <Icon
            className={`h-5 w-5 ${config.color === 'success' ? 'text-green-500' : config.color === 'error' ? 'text-red-500' : 'text-muted-foreground'}`}
          />
          <StatusBadge status={config.color}>{config.label}</StatusBadge>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          <p>Platform: {status?.platform || 'Alpaca'}</p>
          <p>Mode: {status?.isPaper ? 'Paper Trading' : 'Live'}</p>
          {status?.lastSync && <p>Last sync: {formatDate(status.lastSync)}</p>}
          {status?.error && <p className="text-red-500 mt-1">{status.error}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

export default PlatformStatus;
